import { getAppConfig } from '../config';
import { apiGet } from './client';
import type {
  InitUploadRequestV2,
  InitUploadResponseV2,
  StartAnalysisRequestV2,
  JobResponseV2,
  LogisticsShippingKpiJobV2,
  JobListResponseV2,
  FileDownloadResponse,
} from '@/lib/types/feature/logistics-shipping-kpi-v2';
import type { LatestExecutionDefaults } from '@/lib/types/feature/logistics-shipping-kpi';

/**
 * 絶対URLを構築するヘルパー関数
 */
function buildAbsoluteUrl(baseUrl: string, path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  // baseUrlが相対パス（/で始まる）の場合、現在のoriginと組み合わせる
  if (baseUrl.startsWith('/')) {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
    const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const pathWithSlash = path.startsWith('/') ? path : `/${path}`;
    return `${origin}${base}${pathWithSlash}`;
  }

  // baseUrlが絶対URLの場合は従来の処理
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const pathWithSlash = path.startsWith('/') ? path : `/${path}`;

  return `${base}${pathWithSlash}`;
}

/**
 * 出荷指示書のS3署名URLを取得（v2用）
 */
export async function initUploadV2(
  accountId: string,
  params: InitUploadRequestV2,
  authHeaders?: Record<string, string>,
): Promise<InitUploadResponseV2> {
  const config = getAppConfig();
  if (!config.apiBaseUrl) {
    throw new Error(`API Base URLが設定されていません。現在の環境: ${config.env}`);
  }
  const absoluteUrl = buildAbsoluteUrl(
    config.apiBaseUrl,
    `/${accountId}/logistics-shipping-kpi/analyze/init`,
  );
  const url = new URL(absoluteUrl);

  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(authHeaders || {}),
    },
    body: JSON.stringify({
      shipping_orders: { fileName: params.fileName, contentLength: params.contentLength },
    }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`init 失敗: ${res.status} ${text}`);
  }
  return (await res.json()) as InitUploadResponseV2;
}

/**
 * 署名アップロード済みの出荷指示書を用いて分析開始（v2用）
 */
export async function startAnalysisV2(
  accountId: string,
  jobId: string,
  previousShippingOrdersPath: string,
  request: StartAnalysisRequestV2,
  authHeaders?: Record<string, string>,
): Promise<JobResponseV2> {
  const formData = new FormData();

  if (request.files.targetSellers) formData.append('target_sellers', request.files.targetSellers);
  // v2では sourceWarehouseMapping を使用（FormDataのキーは store_warehouse_mapping のまま）
  if (request.files.sourceWarehouseMapping)
    formData.append('store_warehouse_mapping', request.files.sourceWarehouseMapping);
  if (request.files.excludedSkus) formData.append('excluded_skus', request.files.excludedSkus);
  if (request.files.excludedSellerSkuCombinations)
    formData.append(
      'excluded_seller_sku_combinations',
      request.files.excludedSellerSkuCombinations,
    );
  if (request.files.productGroupFilter)
    formData.append('product_group_filter', request.files.productGroupFilter);
  if (request.files.newProductExclusion)
    formData.append('new_product_exclusion', request.files.newProductExclusion);
  if (request.newProductExclusionType)
    formData.append('new_product_exclusion_type', request.newProductExclusionType);

  const config = getAppConfig();
  const absoluteUrl = buildAbsoluteUrl(
    config.apiBaseUrl,
    `/${accountId}/logistics-shipping-kpi/analyze/start`,
  );
  const url = new URL(absoluteUrl);
  url.searchParams.append('job_id', jobId);
  url.searchParams.append('previous_shipping_orders_path', previousShippingOrdersPath);
  url.searchParams.append('start_date', request.startDate);
  url.searchParams.append('end_date', request.endDate);
  url.searchParams.append('comparison_start_date', request.comparisonStartDate);
  url.searchParams.append('comparison_end_date', request.comparisonEndDate);
  if (request.newProductBaseDate)
    url.searchParams.append('new_product_base_date', request.newProductBaseDate);
  url.searchParams.append('apply_dealing_sku_filter', String(request.applyDealingSkuFilter));
  if (request.selectedProductGroupTypeIds) {
    for (const id of request.selectedProductGroupTypeIds) {
      url.searchParams.append('selected_product_group_type_ids', id);
    }
  }
  if (request.product_group_summary_depths) {
    Object.entries(request.product_group_summary_depths).forEach(([typeId, depth]) => {
      const key = `product_group_summary_depths[${typeId}]`;
      const value = String(depth);
      url.searchParams.append(key, value);
    });
  }
  // 前回実行ファイルのパスをクエリに付与（shipping_orders 以外）
  if (request.previousFiles?.targetSellersPath) {
    url.searchParams.append(
      'previous_target_sellers_path',
      request.previousFiles.targetSellersPath,
    );
  }
  // v2では sourceWarehouseMappingPath を使用（クエリパラメータは previous_store_warehouse_mapping_path のまま）
  if (request.previousFiles?.sourceWarehouseMappingPath) {
    url.searchParams.append(
      'previous_store_warehouse_mapping_path',
      request.previousFiles.sourceWarehouseMappingPath,
    );
  }
  if (request.previousFiles?.excludedSkusPath) {
    url.searchParams.append('previous_excluded_skus_path', request.previousFiles.excludedSkusPath);
  }
  if (request.previousFiles?.excludedSellerSkuCombinationsPath) {
    url.searchParams.append(
      'previous_excluded_seller_sku_combinations_path',
      request.previousFiles.excludedSellerSkuCombinationsPath,
    );
  }
  if (request.previousFiles?.productGroupFilterPath) {
    url.searchParams.append(
      'previous_product_group_filter_path',
      request.previousFiles.productGroupFilterPath,
    );
  }
  if (request.previousFiles?.newProductExclusionPath) {
    url.searchParams.append(
      'previous_new_product_exclusion_path',
      request.previousFiles.newProductExclusionPath,
    );
  }
  // 上位品割合（%）パラメータを追加
  url.searchParams.append(
    'top_product_threshold_percent',
    String(request.topProductThresholdPercent),
  );
  // 非稼働判定窓（日数）パラメータを追加（必須）
  url.searchParams.append('inactive_window_days', String(request.inactiveWindowDays));

  if (request.applyDealingSkuFilter) {
    const requiredParams: Array<[string, number | undefined]> = [
      ['recent_stock_day', request.recentStockDay],
      ['required_stock_quantity', request.requiredStockQuantity],
      ['stock_day_threshold', request.stockDayThreshold],
      ['recent_sales_day', request.recentSalesDay],
      ['required_sales_quantity', request.requiredSalesQuantity],
      ['sales_day_threshold', request.salesDayThreshold],
    ];
    for (const [key, val] of requiredParams) {
      if (val === undefined || val === null) {
        throw new Error(`展開判定パラメータ「${key}」は必須です（チェックON時）`);
      }
      url.searchParams.append(key, String(val));
    }
  }

  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: { ...(authHeaders || {}) },
    body: formData,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`start 失敗: ${res.status} ${text}`);
  }
  return (await res.json()) as JobResponseV2;
}

/**
 * ジョブ一覧取得
 */
export async function listJobsV2(
  accountId: string,
  authHeaders: Record<string, string>,
  limit?: number,
  executedBy?: string,
): Promise<LogisticsShippingKpiJobV2[]> {
  try {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (executedBy) params.append('executed_by', executedBy);

    const url = `/${accountId}/logistics-shipping-kpi/jobs${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiGet<JobListResponseV2>(url, authHeaders);
    return response.jobs;
  } catch (error) {
    throw error;
  }
}

/**
 * ファイルダウンロードURL取得（v2用）
 */
export async function downloadFileV2(
  accountId: string,
  jobId: string,
  fileType: string,
  authHeaders: Record<string, string>,
): Promise<FileDownloadResponse> {
  try {
    const url = `/${accountId}/logistics-shipping-kpi/jobs/${jobId}/download/${fileType}`;
    const response = await apiGet<FileDownloadResponse>(url, authHeaders);
    return response;
  } catch (error) {
    throw error;
  }
}

/**
 * ログインユーザーの最新の成功実行をデフォルト値として取得
 */
export const getLatestExecutionDefaultsV2 = async (
  accountId: string,
  authHeaders?: Record<string, string>,
): Promise<LatestExecutionDefaults> => {
  return apiGet<LatestExecutionDefaults>(
    `/${accountId}/logistics-shipping-kpi/latest-execution-defaults`,
    authHeaders,
  );
};

/**
 * 倉庫出荷効果検証ジョブを削除する
 */
export const deleteJobV2 = async (
  accountId: string,
  jobId: string,
  authHeaders?: Record<string, string>,
): Promise<{ message: string }> => {
  const config = getAppConfig();
  const response = await fetch(
    `${config.apiBaseUrl}/${accountId}/logistics-shipping-kpi/jobs/${jobId}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error || `削除リクエストが失敗しました (${response.status})`;
    throw new Error(errorMessage);
  }

  return response.json();
};
